import React from 'react'

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
  className?: string
  elevated?: boolean
}

export function Card({ children, className = '', elevated = true, ...rest }: CardProps) {
  const baseClasses = 'bg-card rounded-card p-4'
  const elevatedClasses = elevated ? 'shadow-card' : ''
  const classes = `${baseClasses} ${elevatedClasses} ${className}`

  return (
    <div className={classes} {...rest}>
      {children}
    </div>
  )
}

export interface CardHeaderProps {
  children: React.ReactNode
  className?: string
}

export function CardHeader({ children, className = '' }: CardHeaderProps) {
  return (
    <div className={`mb-4 ${className}`}>
      {children}
    </div>
  )
}

export interface CardTitleProps {
  children: React.ReactNode
  className?: string
}

export function CardTitle({ children, className = '' }: CardTitleProps) {
  return (
    <h3 className={`text-lg font-semibold ${className}`}>
      {children}
    </h3>
  )
}

export interface CardContentProps {
  children: React.ReactNode
  className?: string
}

export function CardContent({ children, className = '' }: CardContentProps) {
  return (
    <div className={className}>
      {children}
    </div>
  )
}

export interface BadgeProps {
  children?: React.ReactNode
  text?: string
  variant?: 'success' | 'error' | 'neutral' | 'warning' | 'default' | 'secondary' | 'outline' | 'info'
  className?: string
}

export function Badge({ children, text, variant = 'neutral', className = '' }: BadgeProps) {
  const baseClasses = 'inline-flex items-center px-3 py-1 rounded-full text-sm font-medium'
  
  const variantClasses = {
    success: 'bg-success/10 text-success',
    error: 'bg-error/10 text-error',
    warning: 'bg-primary/10 text-primary',
    neutral: 'bg-muted text-muted-foreground',
    default: 'bg-primary text-primary-foreground',
    secondary: 'bg-secondary text-secondary-foreground',
    outline: 'border border-input bg-background',
    info: 'bg-blue-100 text-blue-800'
  }
  
  const classes = `${baseClasses} ${variantClasses[variant]} ${className}`
  
  return (
    <span className={classes}>
      {children || text}
    </span>
  )
}

export interface ProgressBarProps {
  value: number
  min?: number
  max?: number
  className?: string
  fillClassName?: string
}

export function ProgressBar({ value, min = 0, max = 100, className = '', fillClassName = 'bg-info' }: ProgressBarProps) {
  const percentage = Math.round(((value - min) / (max - min)) * 100)
  
  return (
    <div className={`w-full bg-muted rounded-full h-2 ${className}`}>
      <div 
        className={`${fillClassName} h-2 rounded-full transition-all duration-300`}
        style={{ width: `${Math.min(100, Math.max(0, percentage))}%` }}
      />
    </div>
  )
}

export interface AvatarProps {
  initials: string
  size?: 'sm' | 'md' | 'lg'
  borderColor?: string
  className?: string
}

export function Avatar({ initials, size = 'md', borderColor, className = '' }: AvatarProps) {
  const sizeClasses = {
    sm: 'w-8 h-8 text-sm',
    md: 'w-12 h-12 text-base',
    lg: 'w-16 h-16 text-lg'
  }
  
  const borderStyle = borderColor ? { borderColor, borderWidth: '2px' } : {}
  const classes = `${sizeClasses[size]} rounded-full bg-primary text-primary-foreground flex items-center justify-center font-medium ${className}`
  
  return (
    <div className={classes} style={borderStyle}>
      {initials}
    </div>
  )
}
